import {
  lookupsJson,
  homeClaimCodeToDescription,
  fpcClassesImpactingPrice,
  financialResponsibilityTiersImpactingPrice,
  getFinancialResponsibilityTier,
  getInsuranceScoreGroup,
  pullAutoDataOnlyForAutoPolicyStates,
  altCreditAlgorithmStatesDates,
  noCreditStates
} from '@ourbranch/lookups';
import uniqBy from 'lodash/uniqBy';
import mapValues from 'lodash/mapValues';
import { format } from 'date-fns';

const isDevEnvironment =
  !window.location.host.includes('staff-uat.app') &&
  !window.location.host.includes('staff-preprod.app') &&
  (window.location.host.includes('dev') || window.location.host.includes('localhost'));

// "20200304" to "2020-03-04"
const parseLossDate = (date) => {
  return date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
};

export const getViolationTitle = (violationClass) =>
  lookupsJson.autoViolationClassDescriptions[violationClass]
    ? `Driving violation (type ${violationClass}: ${lookupsJson.autoViolationClassDescriptions[violationClass]})`
    : `Driving violation (code ${violationClass})`;

export const getSourceLabel = ({ source, isCourtRecord }) => {
  if (isCourtRecord) {
    return 'Court record';
  }
  if (source === 'claims') {
    return 'Claim history';
  }
  if (source === 'mvr') {
    return 'MVR';
  }
  return source || 'Unknown';
};

const getHomeFactors = (offer) => {
  const homeFactors = [];
  const datesAlreadyShown = new Set();
  const homeClaims =
    offer?.quote?.homeClaimDates && Object.keys(offer?.quote?.homeClaimDates).length !== 0
      ? JSON.parse(offer.quote.homeClaimDates)
      : {};
  const uniqueHomeClaims = mapValues(homeClaims, (claim) => uniqBy(claim, 'class'));
  Object.keys(uniqueHomeClaims).forEach((date) => {
    const claimDetails = uniqueHomeClaims[date][0];
    const description = homeClaimCodeToDescription[claimDetails.class];
    homeFactors.push({
      title: description ? `${description} claim` : `Home claim (type ${claimDetails.class})`,
      date,
      source: 'Claim history'
    });
    datesAlreadyShown.add(date);
  });

  const uniqueHomeClaimsForUw = offer?.quote?.homeClaimsForUW
    ? uniqBy(offer?.quote?.homeClaimsForUW, (c) => c.lossTypeCode && c.lossDate)
    : [];

  uniqueHomeClaimsForUw
    ?.filter((claim) => {
      const formattedDate = parseLossDate(claim.lossDate);
      return !datesAlreadyShown.has(formattedDate);
    })
    .forEach((claim) => {
      homeFactors.push({
        title: `Home claim (type ${claim.lossTypeCode})`,
        date: parseLossDate(claim.lossDate),
        source: 'Claim history'
      });
    });

  // #33939 — farm loss/claims live on the canonical `lossHistory` (A+-pulled + manual); the legacy
  // `homeClaimsForUW` is cleared on farm offers, so surface non-ignored loss-history claims here too.
  // `lossDate` is nullable on the shared entry type, so skip entries without a date rather than let
  // parseLossDate throw and break the whole sidebar.
  (offer?.quote?.lossHistory || [])
    .filter((entry) => entry && !entry.ignore && entry.lossDate)
    .forEach((entry) => {
      const formattedDate = parseLossDate(entry.lossDate);
      if (!formattedDate || datesAlreadyShown.has(formattedDate)) {
        return;
      }
      homeFactors.push({
        title: entry.causeOfLoss ? `Claim: ${entry.causeOfLoss}` : 'Claim history',
        date: formattedDate,
        source: 'Claim history'
      });
      datesAlreadyShown.add(formattedDate);
    });

  if (offer.quote?.home?.fpc && fpcClassesImpactingPrice.includes(offer.quote.home.fpc)) {
    homeFactors.push({
      title: `Public Protection Class: ${offer.quote.home.fpc}`,
      description:
        'The Public Protection Class, determined by the Insurance Services Organization, indicates how quickly and easily the local fire department will be able to address a fire at a home’s location. A higher protection class can result in an increased cost to insure a home.',
      isPropertyFactor: true
    });
  }

  const today = new Date();
  if (offer.quote?.home?.roofYear && today.getFullYear() - offer.quote.home.roofYear > 5) {
    homeFactors.push({
      title: `Roof year: ${offer.quote.home.roofYear}`,
      description:
        'The age, type of materials used, and shape of a roof can have an impact on the cost to insure a home.',
      isPropertyFactor: true
    });
  }

  if (offer.quote?.home?.constructionType === 'B') {
    homeFactors.push({
      title: 'Construction type: Brick',
      description:
        "The home's construction type is primarily Brick, as opposed to wood studs. Brick construction can cost more to repair or replace than wood frame construction.",
      isPropertyFactor: true
    });
  }
  return homeFactors.length ? homeFactors : null;
};

const getPersonFactors = ({ person, includeHomeFactors, includeAutoFactors, state, rateControlDate, people }) => {
  const rateControlDateAsInt = Number(format(new Date(rateControlDate), 'yyyyMMdd'));
  const personFactors = [];
  const autoViolations =
    person.autoViolationDates && Object.keys(person.autoViolationDates).length !== 0
      ? JSON.parse(person.autoViolationDates)
      : {};
  const onlyChoosingHome = includeHomeFactors && !includeAutoFactors;
  const dontShowUDR = onlyChoosingHome && pullAutoDataOnlyForAutoPolicyStates[state];

  // mock court record violation for dev bc court record data comes from TransUnion and we don't run it in dev
  // don't mock this in uat
  if (isDevEnvironment) {
    if (person.isPrimary && autoViolations['2023-11-10'] === undefined) {
      autoViolations['2023-11-10'] = [{ class: 'SPD', points: 2, source: 'mvr', miPoints: 0, isCourtRecord: true }];
    }
  }

  const uniqueAutoViolations = mapValues(autoViolations, (violation) => uniqBy(violation, 'class'));

  Object.keys(uniqueAutoViolations).forEach((violationDate) => {
    const violationsClone = [...uniqueAutoViolations[violationDate]];
    // if we have some claims-sourced violations, we only look at those:
    const claimsViolations = violationsClone.filter((v) => v.source === 'claims');
    const violations = claimsViolations.length >= 1 ? claimsViolations : violationsClone;

    // we will operate on the cloned copy to keep the original intact
    const violation = violations
      .sort((a, b) => {
        // first sorting criteria is by points
        if (a.points !== b.points) {
          return b.points - a.points;
        }
        // same points, so go alphabetically A-Z and don't worry about ties
        return a.class.toUpperCase() < b.class.toUpperCase() ? -1 : 1;
      })
      .shift();

    if (violation.class === 'UDR' && dontShowUDR) {
      return;
    }
    const isCourtRecord = !!violation.isCourtRecord;
    const sourceLabel = getSourceLabel({ source: violation.source, isCourtRecord });
    personFactors.push({
      title: getViolationTitle(violation.class),
      date: violationDate,
      source: sourceLabel,
      isCourtRecord
    });
  });

  if (person.isPrimary && !noCreditStates[state]) {
    const autoInsuranceScore = Number(person.insuranceScore?.autoTotal || person.insuranceScore?.total);
    const homeInsuranceScore = Number(person.insuranceScore?.homeTotal || person.insuranceScore?.total);

    // Check if there's a co-applicant
    const coApplicant = people && people.find((p) => !p.isPrimary);
    const coApplicantHomeScore = Number(coApplicant?.insuranceScore?.homeTotal || coApplicant?.insuranceScore?.total);

    // Only blend if both have home scores, otherwise use primary's score
    const scoreToUse =
      coApplicantHomeScore && homeInsuranceScore ? (coApplicantHomeScore + homeInsuranceScore) / 2 : homeInsuranceScore;
    const scoreLabel = coApplicantHomeScore ? 'Blended Insurance' : 'Insurance';

    const financialResponsibilityTier =
      includeAutoFactors &&
      getFinancialResponsibilityTier(
        autoInsuranceScore,
        autoInsuranceScore,
        altCreditAlgorithmStatesDates[state] && rateControlDateAsInt >= altCreditAlgorithmStatesDates[state],
        state
      );
    const insuranceScoreGroup = includeHomeFactors && !noCreditStates[state] && getInsuranceScoreGroup(scoreToUse);
    const insuranceScoreTooltip =
      'An insurance score is not the same thing as a credit score and they are calculated differently.';

    if (!(includeHomeFactors && includeAutoFactors)) {
      if (
        financialResponsibilityTier &&
        financialResponsibilityTiersImpactingPrice.includes(financialResponsibilityTier)
      ) {
        personFactors.push({
          title: `Financial Responsibility Tier: ${financialResponsibilityTier}`,
          description:
            'The tier is based on a range of A1 to R1 and reflects multiple factors, including your insurance score. Lower tier placement can impact the cost to insure a vehicle.',
          moreInfo: true,
          moreInfoTooltip: `Tier placement is based on factors that are part of your credit history and can be used to predict the likelihood of a claim. ${insuranceScoreTooltip}`
        });
      }
      if (insuranceScoreGroup) {
        personFactors.push({
          title: `${scoreLabel} Score Group: ${insuranceScoreGroup}/50`,
          description:
            'The insurance group is based on multiple factors that are a part of your credit history and can be used to predict the likelihood of a claim. Lower scores can impact the cost to insure a home.',
          moreInfo: true,
          moreInfoTooltip: insuranceScoreTooltip
        });
      }
    } else {
      personFactors.push({
        title: `${scoreLabel} Score Group: ${insuranceScoreGroup}/50 · Financial Responsibility Tier: ${financialResponsibilityTier}`,
        description:
          'The auto tier range is from A1 to R1 and score grouping is from 1 to 50. Insurance Score Groups and tiers are determined based on multiple factors, including your insurance score, and can be used to predict the likelihood of a claim. Lower scores or tier placement can have an impact on the price and/or eligibility of your bundled insurance.',
        moreInfo: true,
        moreInfoTooltip: insuranceScoreTooltip
      });
    }
  }

  return personFactors.length ? personFactors : null;
};

const getPersonFactorObjKey = (person) => {
  return `${person.firstName}/${person.lastName}/${person.dateOfBirth}`;
};

const getPeopleFactors = ({ people, includeHomeFactors, includeAutoFactors, state, rateControlDate }) => {
  const peopleFactors = {};

  if (people) {
    people.forEach((person) => {
      const personFactors = getPersonFactors({
        person,
        includeHomeFactors,
        includeAutoFactors,
        state,
        rateControlDate,
        people
      });
      if (personFactors) {
        const key = getPersonFactorObjKey(person);
        peopleFactors[key] = personFactors;
      }
    });
  }

  return Object.keys(peopleFactors).length ? peopleFactors : null;
};

const getCarFactors = (car) => {
  const carFactors = [];

  const lastSevereIncidentDate = car?.lastSevereIncidentDate || (isDevEnvironment ? '2020-09-14' : null);

  if (lastSevereIncidentDate) {
    carFactors.push({
      title: `Severe incident identified on ${car.make} ${car.model} ${car.year}`,
      date: lastSevereIncidentDate,
      source: 'Vehicle history'
    });
  }
  return Object.keys(carFactors).length ? carFactors : null;
};

const getCarsFactors = (offer) => {
  const carsFactors = {};
  const { cars } = offer?.quote || {};

  if (cars && cars.length) {
    cars.forEach((car) => {
      const carFactors = getCarFactors(car);
      if (carFactors) {
        carsFactors[car.VIN] = carFactors;
      }
    });
  }
  return Object.keys(carsFactors).length ? carsFactors : null;
};

export const getFactorsImpactingPrice = ({ offer, homeSelected, autoSelected, homeRejected, autoRejected }) => {
  if (offer) {
    const homeFactors = getHomeFactors(offer);
    const carFactors = getCarsFactors(offer);
    const people = offer?.quote?.drivers?.length ? offer.quote.drivers : offer?.quote?.people;
    const peopleFactors = getPeopleFactors({
      people,
      includeHomeFactors: homeSelected || homeRejected,
      includeAutoFactors: autoSelected || autoRejected,
      state: offer.quote.correctedAddress.state,
      rateControlDate: offer.quote.global.rateControlDate
    });

    if (!homeFactors && !carFactors && !peopleFactors) {
      return null;
    }

    return {
      homeFactors,
      peopleFactors,
      carFactors
    };
  }
};
